import api from './api';

/**
 * authService — Arquitetura httpOnly Cookie
 *
 * Mudança de paradigma:
 * - ANTES: token JWT era armazenado em localStorage (vulnerável a XSS)
 * - AGORA: o backend seta o JWT em um cookie httpOnly via Set-Cookie.
 *           O JavaScript nunca vê o token. O browser o envia automaticamente
 *           em toda requisição para a API (via withCredentials: true no Axios).
 *
 * O que permanece no sessionStorage:
 *   - Apenas dados PÚBLICOS de UI: { usuarioId, nome, email, papel }
 *   - Usados somente para exibir nome/papel na interface, não para autenticação.
 *   - sessionStorage é limpo automaticamente quando o browser fecha a aba.
 */
const authService = {

  login: async (email, senha) => {
    try {
      const response = await api.post('/auth/2fa/login', { email, senha });

      if (!response.data.requires2Fa) {
        // Salvar somente dados de UI — o token está no cookie httpOnly e é invisível
        const { usuarioId, nome, papel } = response.data;
        sessionStorage.setItem('usuario', JSON.stringify({ usuarioId, nome, email, papel }));
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || 'Erro ao fazer login';
    }
  },

  verify2FA: async (email, codigo, mfaToken) => {
    try {
      // mfaToken identifica a sessão pré-auth no backend
      const response = await api.post('/auth/2fa/verify-code', { email, codigo, mfaToken });
      const { usuarioId, nome, papel } = response.data;

      // Após 2FA validado, o cookie jwt já foi setado pelo backend
      sessionStorage.setItem('usuario', JSON.stringify({ usuarioId, nome, email, papel }));

      return response.data;
    } catch (error) {
      throw error.response?.data || 'Código 2FA inválido';
    }
  },

  setup2FA: async () => {
    try {
      const response = await api.post('/perfil/2fa/setup');
      return response.data;
    } catch (error) {
      throw error.response?.data || 'Erro ao gerar QR Code';
    }
  },

  confirmSetup2FA: async (codigo) => {
    try {
      const usuario = JSON.parse(sessionStorage.getItem('usuario'));
      const response = await api.post('/auth/2fa/verify-code', {
        email: usuario?.email,
        codigo: codigo,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || 'Código inválido ao confirmar ativação';
    }
  },

  /**
   * Logout:
   * 1. Chama o backend para invalidar o cookie jwt (maxAge=0)
   * 2. Remove os dados de UI do sessionStorage
   */
  logout: async () => {
    try {
      await api.post('/auth/2fa/logout');
    } catch (error) {
      // Mesmo que o backend falhe, limpamos o estado local
      console.warn('Aviso: falha ao contactar backend no logout.', error);
    } finally {
      sessionStorage.removeItem('usuario');
    }
  },

  getUsuarioAtual: () => {
    const usuario = sessionStorage.getItem('usuario');
    return usuario ? JSON.parse(usuario) : null;
  },

  /**
   * A autenticação agora é via cookie httpOnly — invisível ao JS.
   * Usamos a presença dos dados de UI como indicador de sessão ativa.
   * O backend é a fonte de verdade real (retorna 401 se o cookie expirar).
   */
  isAutenticado: () => {
    return !!sessionStorage.getItem('usuario');
  },
};

export default authService;